import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { PhotoIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface PhotoUploadProps {
  onUpload: (file: File) => Promise<void>;
  maxSize?: number; // in bytes
  acceptedTypes?: string[];
  multiple?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

export default function PhotoUpload({ 
  onUpload, 
  maxSize = MAX_FILE_SIZE,
  acceptedTypes = Object.keys(ACCEPTED_FILE_TYPES),
  multiple = false
}: PhotoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize) {
      return `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`;
    }

    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return `Only ${acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} files are allowed`;
    }

    // Check for potential security issues
    const suspiciousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.com', '.pif'];
    const hasDoubleExtension = suspiciousExtensions.some(ext => 
      file.name.toLowerCase().includes(ext)
    );
    
    if (hasDoubleExtension) {
      return 'Invalid file type detected';
    }

    return null;
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      // Validate the file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      try {
        setIsUploading(true);
        setError(null);
        setSuccess(false);
        setUploadProgress(0);

        // Simulate upload progress (replace with actual progress tracking if available)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 100);

        await onUpload(file);
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        setSuccess(true);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
          setUploadProgress(0);
        }, 3000);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload photo');
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, maxSize, acceptedTypes]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize,
    multiple,
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      if (!rejection) return;

      const errorCode = rejection.errors[0]?.code;
      
      if (errorCode === 'file-too-large') {
        setError(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      } else if (errorCode === 'file-invalid-type') {
        setError('Only JPG, JPEG, PNG, and WebP files are allowed');
      } else {
        setError('Invalid file selected');
      }
    },
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-200
          ${isDragActive 
            ? 'border-blue-400 bg-cyan-50' 
            : isUploading 
              ? 'border-gray-300 bg-slate-50/80 cursor-not-allowed'
              : error
                ? 'border-red-300 bg-red-50 hover:border-red-400'
                : success
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-slate-50/80'
          }
        `}
      >
        <input {...getInputProps()} disabled={isUploading} />
        
        <div className="space-y-4">
          {/* Icon */}
          <div className="mx-auto w-12 h-12">
            {isUploading ? (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            ) : success ? (
              <CheckCircleIcon className="w-12 h-12 text-green-500" />
            ) : error ? (
              <ExclamationCircleIcon className="w-12 h-12 text-red-500" />
            ) : (
              <PhotoIcon className="w-12 h-12 text-slate-400" />
            )}
          </div>

          {/* Text */}
          {isUploading ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Uploading...</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500">{uploadProgress}%</p>
            </div>
          ) : success ? (
            <p className="text-sm text-green-600 font-medium">Upload successful!</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">
                {isDragActive ? 'Drop the photo here...' : 'Drag and drop a photo here, or click to select'}
              </p>
              <p className="text-xs text-slate-500">
                Supported formats: JPG, PNG, WebP • Max size: {formatFileSize(maxSize)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-auto flex-shrink-0 text-red-400 hover:text-red-600"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 